/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  InputLabel,
  Alert,
} from "@mui/material";
import StyledTextField from "../text-field/styled-text-field";
import { SearchAddress } from "./types";
import { useSearch } from "../search/search-context";
import { normalizeString } from "../helpers";
import useAddressLookup from "./use-address-lookup";

export interface AddressLookupProps {
  handleChange: (name: string, value: string | null) => void;
}

const AddressLookup = ({ handleChange }: AddressLookupProps) => {
  const { fullAddress, pafudprn } = useSearch();
  const [postcodeInput, setPostcodeInput] = useState("");
  const [selectedAddress, setSelectedAddress] = useState<SearchAddress | null>(
    fullAddress && pafudprn ? { fullAddress, pafudprn } : null,
  );
  const { fetchAddresses, addressOptions, error, loading } = useAddressLookup();

  const onAddressChange = (address: SearchAddress | null) => {
    setSelectedAddress(address);
    handleChange("fullAddress", address ? address.fullAddress : "");
    handleChange("pafudprn", address ? address.pafudprn : "");
    handleChange("postcode", postcodeInput);
  };

  useEffect(() => {
    if (postcodeInput.length >= 4) {
      fetchAddresses(postcodeInput);
    }
  }, [postcodeInput]);

  useEffect(() => {
    if (fullAddress && pafudprn) {
      setSelectedAddress({ fullAddress, pafudprn });
      setPostcodeInput("");
    } else {
      setSelectedAddress(null);
      setPostcodeInput("");
    }
  }, [fullAddress, pafudprn]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return !selectedAddress ? (
    <>
      <InputLabel
        shrink
        htmlFor="address-input"
        sx={{ position: "relative", marginBottom: "2px" }}
      >
        Enter postcode
      </InputLabel>

      <Autocomplete
        id="address-input"
        options={addressOptions}
        getOptionLabel={(option) => option.fullAddress}
        loading={loading}
        value={selectedAddress}
        inputValue={postcodeInput}
        onInputChange={(_, newInputValue) => {
          const cleaned = normalizeString(newInputValue);
          setPostcodeInput(cleaned);
        }}
        onChange={(_, value) => onAddressChange(value)}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            variant="outlined"
            fullWidth
            value={postcodeInput}
            slotProps={{
              input: {
                ...params.InputProps,
                value: postcodeInput,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.fullAddress}>
            <Typography fontSize={12}>{option.fullAddress}</Typography>
          </Box>
        )}
      />
    </>
  ) : (
    <Box display="flex" alignItems="center" gap={1}>
      <Box flexGrow={1}>
        <Typography variant="subtitle2">Selected Address:</Typography>
        <Typography variant="body2" data-testid="selected-address">
          {selectedAddress.fullAddress}
        </Typography>
      </Box>
      <Box
        sx={{
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: "1.2rem",
          color: "gray",
          "&:hover": { color: "black" },
          transition: "color 0.3s ease",
          marginRight: 2,
        }}
        onClick={() => {
          setSelectedAddress(null);
          setPostcodeInput("");
        }}
      >
        ×
      </Box>
    </Box>
  );
};

export default AddressLookup;
